import type {Collection, CollectionStateBase, Node} from '@react-types/shared';
import type {Key} from 'react';
import {useCallback, useEffect, useMemo, useRef} from 'react';
import {useCollection} from '../collections';
import type {MultipleSelectionStateProps} from '../../@react-stately/selection';
import {
  SelectionManager,
  useMultipleSelectionState,
} from '../../@react-stately/selection';

import {ListCollection} from './ListCollection';

export interface ListProps<T>
  extends CollectionStateBase<T>,
    MultipleSelectionStateProps {
  /** Filter function to generate a filtered list of nodes. */
  filter?: (nodes: Iterable<Node<T>>) => Iterable<Node<T>>;
  /** @private */
  suppressTextValueWarning?: boolean;
}

export interface ListState<T> {
  /** A collection of items in the list. */
  collection: Collection<Node<T>>;

  /** A set of items that are disabled. */
  disabledKeys: Set<Key>;

  /** A selection manager to read and update multiple selection state. */
  selectionManager: SelectionManager;
}

/**
 * Provides state management for list-like components. Handles building a collection
 * of items from props, and manages multiple selection state.
 */
export function useListState<T extends object>(
  props: ListProps<T>,
): ListState<T> {
  const {filter} = props;

  const selectionState = useMultipleSelectionState(props);
  const disabledKeys = useMemo(
    () => (props.disabledKeys ? new Set(props.disabledKeys) : new Set<Key>()),
    [props.disabledKeys],
  );

  const factory = useCallback(
    (nodes) =>
      filter
        ? new ListCollection(filter(nodes))
        : new ListCollection(nodes as Iterable<Node<T>>),
    [filter],
  );
  const context = useMemo(
    () => ({suppressTextValueWarning: props.suppressTextValueWarning}),
    [props.suppressTextValueWarning],
  );

  const collection = useCollection(props, factory, context);

  const selectionManager = useMemo(
    () => new SelectionManager(collection, selectionState),
    [collection, selectionState],
  );

  // Reset focused key if that item is deleted from the collection.
  const cachedCollection = useRef<Collection<Node<T>> | null>(null);
  useEffect(() => {
    if (
      selectionState.focusedKey != null &&
      !collection.getItem(selectionState.focusedKey)
    ) {
      const startItem = cachedCollection.current!.getItem(
        selectionState.focusedKey,
      )!;
      const cachedItemNodes = [...cachedCollection.current!.getKeys()]
        .map((key) => {
          const itemNode = cachedCollection.current!.getItem(key);
          return itemNode?.type === 'item' ? itemNode : null;
        })
        .filter((node) => node !== null);
      const itemNodes = [...collection.getKeys()]
        .map((key) => {
          const itemNode = collection.getItem(key);
          return itemNode?.type === 'item' ? itemNode : null;
        })
        .filter((node) => node !== null);
      const diff = cachedItemNodes.length - itemNodes.length;
      let index: number = Math.min(
        diff > 1 ? Math.max(startItem.index! - diff + 1, 0) : startItem.index!,
        itemNodes.length - 1,
      );
      let newNode: Node<T> | null | undefined;
      while (index >= 0) {
        if (!selectionManager.isDisabled(itemNodes[index]!.key)) {
          newNode = itemNodes[index];
          break;
        }
        // Find next, not disabled item.
        if (index < itemNodes.length - 1) {
          index++;
          // Otherwise, find previous, not disabled item.
        } else {
          if (index > startItem.index!) {
            index = startItem.index!;
          }
          index--;
        }
      }
      selectionState.setFocusedKey(newNode ? newNode.key : null);
    }
    cachedCollection.current = collection;
  }, [collection, selectionManager, selectionState, selectionState.focusedKey]);

  return {
    collection,
    disabledKeys,
    selectionManager,
  };
}
